import React, { useCallback, useContext, useEffect, useState } from 'react';

import AccountPickerContext from 'contexts/AccountPickerContext';
import buildCardLayout from 'contextProviders/WidgetGalleryProvider/buildCardLayout';
import findStartingY from 'contextProviders/WidgetGalleryProvider/findStartingY';
import aguid from 'lib/aguid';

import TextCardForm from './TextCardForm';

const TextCardFormContainer = ({
  close,
  isOpen,
  setCurrentCanvas,
  startEditing,
  canvasMode,
}: {
  close: () => void;
  isOpen: boolean;
  setCurrentCanvas: React.Dispatch<React.SetStateAction<Canvas>>;
  startEditing: () => void;
  canvasMode: CanvasMode;
}) => {
  const { accountRef } = useContext(AccountPickerContext);
  const [textContentId, setTextContentId] = useState<string>(aguid());
  const [text, setText] = useState<string>('');
  const [isCenterAlign, setIsCenterAlign] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen) {
      setTextContentId(aguid());
      setText('');
      setIsCenterAlign(false);
    }
  }, [isOpen]);

  const persist = useCallback(
    ({
      nextText,
      nextIsCenterAlign,
    }: {
      nextText: string;
      nextIsCenterAlign: boolean;
    }) => {
      if (process.env.NODE_ENV === 'test') {
        return;
      }

      accountRef
        .collection('textCardContents')
        .doc(textContentId)
        .set({ textContent: nextText, isCenterAlign: nextIsCenterAlign });
    },
    [accountRef, textContentId],
  );

  const onChange = useCallback(
    (content: string) => {
      setText(content);
      persist({ nextText: content, nextIsCenterAlign: isCenterAlign });
    },
    [persist, isCenterAlign],
  );

  const onAlignChanged = useCallback(
    (newAlign: boolean) => {
      setIsCenterAlign(newAlign);
      persist({ nextText: text, nextIsCenterAlign: newAlign });
    },
    [persist, text],
  );

  const onSave = useCallback(() => {
    setCurrentCanvas((c) => {
      const newCard = (() => {
        return {
          layout: buildCardLayout(0, findStartingY(c.cards, canvasMode)),
          content: {
            type: 'Text' as 'Text',
            textContentId,
          },
        };
      })();

      return {
        ...c,
        cards: [...c.cards, newCard],
      };
    });
    startEditing();
    close();
  }, [canvasMode, close, setCurrentCanvas, startEditing, textContentId]);

  return (
    <TextCardForm
      text={text}
      onChange={onChange}
      onSave={onSave}
      close={close}
      isOpen={isOpen}
      isCenterAlign={isCenterAlign}
      onAlignChanged={onAlignChanged}
    />
  );
};

export default TextCardFormContainer;
